import React from 'react';
import clsx from 'clsx';

export interface AIProgressBarProps {
  /** 0–100 from the engine, or null when it hasn't said. */
  percent: number | null;
  tone?: 'champagne' | 'muted' | 'danger';
  className?: string;
}

/**
 * THE progress bar for AI runs — a champagne fill on a sunken track.
 *
 * `percent === null` is the honest case, not an error: the bar becomes a
 * moving sliver that reads as "working". The G1 bars it replaces filled a
 * `from-info-500 to-info-900` gradient from a number the client had made up.
 */
const AIProgressBar: React.FC<AIProgressBarProps> = ({
  percent,
  tone = 'champagne',
  className,
}) => (
  <div
    className={clsx('h-1.5 rounded-full bg-surface-sunken overflow-hidden', className)}
    role="progressbar"
    aria-valuenow={percent ?? undefined}
    aria-valuemin={0}
    aria-valuemax={100}
  >
    <div
      className={clsx(
        'h-full rounded-full transition-[width] duration-500 ease-slide',
        tone === 'champagne'
          ? 'bg-champagne'
          : tone === 'danger'
            ? 'bg-danger-400'
            : 'bg-fg-muted/40',
        // No number yet: a moving sliver reads as "working", a full bar lies.
        percent === null && 'w-1/3 animate-pulse',
      )}
      style={percent === null ? undefined : { width: `${percent}%` }}
    />
  </div>
);

export default AIProgressBar;
